import { flattenBlocksDeep, mapBlocksInTableData } from './tableCellBlocks';

/** Hold the checkbox this long to set (or clear) the partial tick. */
export const CHECKBOX_HOLD_MS = 500;

/** Third tick value stored on mission overrides and compared with master. */
export const TICK_INDETERMINATE = 'indeterminate';

export const isTickIndeterminate = (tick) => tick === TICK_INDETERMINATE;
export const isTickChecked = (tick) => tick === true;
export const isTickActive = (tick) => tick === true || tick === TICK_INDETERMINATE;

export const tickToProps = (tick) => ({
  checked: tick === true,
  indeterminate: tick === TICK_INDETERMINATE,
});

export const normalizeTick = (raw) => {
  if (raw === TICK_INDETERMINATE) return TICK_INDETERMINATE;
  return !!raw;
};

export const readMasterTick = (block) => {
  if (block?.props?.indeterminate) return TICK_INDETERMINATE;
  return !!block?.props?.checked;
};

export const readDisplayedTick = (block, overrides) => {
  if (overrides && Object.prototype.hasOwnProperty.call(overrides, block.id)) {
    return normalizeTick(overrides[block.id]);
  }
  return readMasterTick(block);
};

/**
 * Click toggles empty ↔ checked; a partial tick becomes checked.
 * Hold sets partial, or clears a partial tick back to empty.
 */
export const nextCheckboxTick = (current, action) => {
  if (action === 'hold') {
    return current === TICK_INDETERMINATE ? false : TICK_INDETERMINATE;
  }
  return current === TICK_INDETERMINATE ? true : current !== true;
};

export const cloneTickOverrides = (overrides) =>
  overrides && typeof overrides === 'object' && !Array.isArray(overrides)
    ? { ...overrides }
    : {};

export const tickOverridesEqual = (a, b) => {
  const aa = cloneTickOverrides(a);
  const bb = cloneTickOverrides(b);
  const keys = Object.keys(aa);
  if (keys.length !== Object.keys(bb).length) return false;
  return keys.every(
    (k) => Object.prototype.hasOwnProperty.call(bb, k) && aa[k] === bb[k]
  );
};

/**
 * Uncheck-all as mission tick overrides: explicit false for every step the
 * master has checked or partial; mission-only ticks (master empty) drop out.
 * Current displayed ticks must not matter — otherwise a prior uncheck
 * (override false) is dropped, the master tick comes back, and the next
 * click looks like an invert.
 */
export const buildUncheckAllTickOverrides = (blocks) => {
  const next = {};
  for (const block of flattenBlocksDeep(blocks || [])) {
    if (block.type !== 'checkbox') continue;
    if (isTickActive(readMasterTick(block))) next[block.id] = false;
  }
  return next;
};

/** Tree-mutating uncheck-all (library / authoring — writes onto the doc). */
export const clearCheckedBoxes = (list) =>
  (list || []).map((block) => {
    const next = {
      ...block,
      ...(block.type === 'checkbox' && isTickActive(readMasterTick(block))
        ? { props: { ...block.props, checked: false, indeterminate: false } }
        : {}),
      children: block.children?.length
        ? clearCheckedBoxes(block.children)
        : block.children,
    };
    if (next.type === 'table' && next.props?.tableData) {
      const mapped = mapBlocksInTableData(next.props.tableData, clearCheckedBoxes);
      if (mapped.changed) {
        return { ...next, props: { ...next.props, tableData: mapped.tableData } };
      }
    }
    return next;
  });
